import { useState, useEffect } from "react";
import { trpc } from "../../lib/trpc";
import {
  Loader2,
  UploadCloud,
  CheckCircle2,
  Sparkles,
  Check,
  Crown,
  Zap,
  Copy,
  CreditCard,
  ArrowRight,
  ShieldCheck,
  AlertCircle,
  FileText,
  Clock,
  XCircle,
  Eye,
  Gift,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useSearchParams } from "react-router";
import { ReceiptViewerModal } from "../../components/billing/ReceiptViewerModal";
import {
  type BillingPlansConfig,
  DEFAULT_BILLING_CONFIG,
  calculateTierPrice,
} from "@profolio/validators";

export function BillingPage() {
  const trpcContext = trpc.useUtils();
  const [searchParams] = useSearchParams();

  // Queries
  const { data: subData, isLoading: isSubLoading } = trpc.billing.getMySubscription.useQuery(
    undefined,
    { refetchInterval: 5000 }
  );

  const { data: configData } = trpc.billing.getBillingConfig.useQuery(undefined, {
    staleTime: 60000,
  });

  const billingConfig: BillingPlansConfig = configData || DEFAULT_BILLING_CONFIG;

  // State
  const [selectedTier, setSelectedTier] = useState<"pro" | "premium">("pro");
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">("monthly");
  const [paymentMethodTab, setPaymentMethodTab] = useState<"payhere" | "bank">("payhere");

  // Bank transfer step & metadata form
  const [bankStep, setBankStep] = useState<1 | 2 | 3>(1);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewObjectUrl, setPreviewObjectUrl] = useState<string | null>(null);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [depositorName, setDepositorName] = useState("");
  const [transferDate, setTransferDate] = useState(new Date().toISOString().split("T")[0]);
  const [notes, setNotes] = useState("");

  // Modals & Loaders
  const [isUploading, setIsUploading] = useState(false);
  const [isInitiatingPayHere, setIsInitiatingPayHere] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isCancellingPending, setIsCancellingPending] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const subscription = subData;
  const effectiveTier = subData?.effectiveTier || "starter";

  // Check for return parameters from PayHere redirect
  useEffect(() => {
    const paymentStatus = searchParams.get("payment");
    if (paymentStatus === "success") {
      toast.success("Payment completed! Synchronizing your new entitlement...");
      trpcContext.billing.getMySubscription.invalidate();
      trpcContext.profile.getMyProfile.invalidate();
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment checkout was cancelled.");
    }
  }, [searchParams, trpcContext]);

  // Handle local file selection and create client-side object URL
  const handleFileChange = (file: File | null) => {
    if (previewObjectUrl) {
      URL.revokeObjectURL(previewObjectUrl);
      setPreviewObjectUrl(null);
    }
    if (!file) {
      setSelectedFile(null);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Receipt file must be less than 5MB");
      return;
    }
    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewObjectUrl(url);
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (previewObjectUrl) {
        URL.revokeObjectURL(previewObjectUrl);
      }
    };
  }, [previewObjectUrl]);

  // Initiate PayHere Checkout Mutation
  const initiatePayHereMutation = trpc.billing.initiatePayHere.useMutation({
    onSuccess: (data) => {
      const { actionUrl, ...params } = data.checkoutParams;

      const form = document.createElement("form");
      form.method = "POST";
      form.action = actionUrl;
      form.style.display = "none";

      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = String(value);
          form.appendChild(input);
        }
      }

      document.body.appendChild(form);
      toast.info("Redirecting to secure PayHere checkout...");
      form.submit();
    },
    onError: (err) => {
      setIsInitiatingPayHere(false);
      toast.error(err.message || "Failed to initiate PayHere checkout");
    },
  });

  const handlePayHereCheckout = async (targetTier?: "pro" | "premium") => {
    try {
      setIsInitiatingPayHere(true);
      await initiatePayHereMutation.mutateAsync({
        tier: targetTier || selectedTier,
        billingCycle,
      });
    } catch {
      setIsInitiatingPayHere(false);
    }
  };

  // Cancel Subscription Mutation
  const cancelSubscriptionMutation = trpc.billing.cancelSubscription.useMutation({
    onSuccess: () => {
      toast.success("Subscription cancelled. Your entitlement has safely reverted to Starter.");
      trpcContext.billing.getMySubscription.invalidate();
      trpcContext.profile.getMyProfile.invalidate();
      setIsCancelling(false);
    },
    onError: (err) => {
      setIsCancelling(false);
      toast.error(err.message || "Failed to cancel subscription");
    },
  });

  // Cancel Pending Bank Transfer Mutation
  const cancelPendingMutation = trpc.billing.cancelPendingBankTransfer.useMutation({
    onSuccess: () => {
      toast.success("Pending bank transfer cancelled. You can now select a new plan or payment method.");
      trpcContext.billing.getMySubscription.invalidate();
      setIsCancellingPending(false);
      setBankStep(1);
      setSelectedFile(null);
    },
    onError: (err) => {
      setIsCancellingPending(false);
      toast.error(err.message || "Failed to cancel pending transfer");
    },
  });

  // Submit Bank Transfer Mutation
  const submitTransferMutation = trpc.billing.submitBankTransfer.useMutation({
    onSuccess: () => {
      toast.success("Bank transfer receipt submitted successfully. Please wait for staff approval.");
      trpcContext.billing.getMySubscription.invalidate();
      setSelectedFile(null);
      setBankStep(1);
      setIsUploading(false);
    },
    onError: (err) => {
      toast.error(err.message);
      setIsUploading(false);
    },
  });

  const getUploadUrlMutation = trpc.media.getUploadUrl.useMutation();

  const handleBankTransferSubmit = async () => {
    if (!selectedFile) {
      toast.error("Please upload your bank deposit receipt");
      return;
    }
    if (!referenceNumber.trim()) {
      toast.error("Please enter the bank transaction reference number");
      return;
    }
    if (!depositorName.trim()) {
      toast.error("Please enter the depositor's name");
      return;
    }

    try {
      setIsUploading(true);

      const { url, publicUrl } = await getUploadUrlMutation.mutateAsync({
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileSize: selectedFile.size,
        category: "document",
      });

      const res = await fetch(url, {
        method: "PUT",
        headers: { "Content-Type": selectedFile.type },
        body: selectedFile,
      });

      if (!res.ok) {
        throw new Error("Failed to upload receipt to secure storage");
      }

      await submitTransferMutation.mutateAsync({
        receiptUrl: publicUrl,
        tier: selectedTier,
        billingCycle,
        referenceNumber: referenceNumber.trim(),
        depositorName: depositorName.trim(),
        transferDate,
        notes: notes.trim() || undefined,
      });
    } catch (error: any) {
      toast.error(error.message || "Failed to submit bank transfer");
      setIsUploading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  if (isSubLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Derive status booleans
  const isPending =
    subscription?.status === "pending_approval" || subscription?.status === "payment_pending";
  const isRejected = subscription?.status === "cancelled" && Boolean(subscription?.rejectionReason);
  const isExpired =
    subscription?.status === "expired" ||
    (subscription?.status === "active" && effectiveTier === "starter");
  const isPaidActive = subscription?.status === "active" && effectiveTier !== "starter";
  const isPremium = effectiveTier === "premium" && isPaidActive;
  const isPro = effectiveTier === "pro" && isPaidActive;

  // Active promotional offer
  const activeOffer =
    billingConfig.activeOffer?.enabled && billingConfig.activeOffer.discountPercent > 0
      ? billingConfig.activeOffer
      : null;

  // Price calculations with dynamic configuration
  const proMonthly = calculateTierPrice(billingConfig, "pro", "monthly");
  const proAnnual = calculateTierPrice(billingConfig, "pro", "annual");
  const premiumMonthly = calculateTierPrice(billingConfig, "premium", "monthly");
  const premiumAnnual = calculateTierPrice(billingConfig, "premium", "annual");

  const currentPrice =
    selectedTier === "pro"
      ? billingCycle === "annual"
        ? proAnnual
        : proMonthly
      : billingCycle === "annual"
      ? premiumAnnual
      : premiumMonthly;

  const receiptMetadata = (subscription?.receiptMetadata as any) || {};

  return (
    <div className="max-w-4xl mx-auto py-6 px-4 space-y-8">
      {/* ── Page Header ── */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Billing & Membership</h1>
        <p className="text-xs text-muted-foreground mt-0.5">
          Manage your academic presence, visual styling privileges, custom domains, and subscription tier.
        </p>
      </div>

      {/* ── Active Promotional Discount Banner (For non-peak users) ── */}
      {activeOffer && !isPremium && (
        <div className="rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-amber-500/10 to-primary/5 p-4 flex items-center justify-between gap-4 shadow-xs">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/20 text-primary flex items-center justify-center shrink-0">
              <Gift className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary text-primary-foreground shadow-xs">
                  {activeOffer.badgeText || "SPECIAL OFFER"}
                </span>
                <span className="text-xs font-bold text-foreground">{activeOffer.title}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {activeOffer.description ||
                  `Save ${activeOffer.discountPercent}% on Pro and Premium memberships.`}
              </p>
            </div>
          </div>
          <div className="hidden sm:block text-right">
            <span className="text-xs font-bold text-primary">{activeOffer.discountPercent}% OFF</span>
            <span className="block text-[10px] text-muted-foreground">Applied at checkout</span>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          STATE 1: ACTIVE PREMIUM (PEAK TIER) - NO CHECKOUT FORMS DISPLAYED
          ══════════════════════════════════════════════════════════════════════ */}
      {isPremium ? (
        <div className="space-y-6">
          {/* Luxury Gold Peak Status Card */}
          <div className="rounded-2xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-card to-card p-6 shadow-sm relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl -z-10 pointer-events-none" />

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6 border-b border-border/80">
              <div className="flex items-center gap-3.5">
                <div className="h-12 w-12 rounded-xl bg-amber-500/20 text-amber-500 flex items-center justify-center border border-amber-500/30 shadow-xs">
                  <Crown className="h-6 w-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500 text-white shadow-xs">
                      LUXURY PEAK TIER
                    </span>
                    <span className="text-xs font-semibold text-emerald-500 flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Active Member
                    </span>
                  </div>
                  <h2 className="text-xl font-bold text-foreground mt-1">Premium Scholar Membership</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    You have unlocked the highest tier on Profolio. All premium features, luxury themes, and academic tools are fully active.
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 self-end sm:self-auto">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCancelling}
                  onClick={() => {
                    if (
                      confirm(
                        "Are you sure you want to cancel your Premium subscription? Your publications and profile content will remain safely preserved in the database, but your live public portfolio will revert to Starter styling."
                      )
                    ) {
                      setIsCancelling(true);
                      cancelSubscriptionMutation.mutate();
                    }
                  }}
                  className="text-xs text-muted-foreground hover:text-destructive hover:border-destructive/40"
                >
                  {isCancelling ? "Cancelling..." : "Cancel Subscription"}
                </Button>
              </div>
            </div>

            {/* Renewal / Expiry Details */}
            <div className="pt-5 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
              <div className="p-3 rounded-lg bg-card/60 border border-border/60">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Next Renewal Date
                </span>
                <span className="font-semibold text-foreground text-sm mt-0.5 block">
                  {subscription?.expiresAt
                    ? new Date(subscription.expiresAt).toLocaleDateString(undefined, {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })
                    : "Lifetime / Manual"}
                </span>
              </div>
              <div className="p-3 rounded-lg bg-card/60 border border-border/60">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Days Remaining
                </span>
                <span className="font-bold text-amber-500 text-sm mt-0.5 block">
                  {subscription?.daysRemaining !== null ? `${subscription?.daysRemaining} Days` : "Active"}
                </span>
              </div>
              <div className="p-3 rounded-lg bg-card/60 border border-border/60">
                <span className="text-[10px] uppercase font-semibold text-muted-foreground block">
                  Billing Cycle
                </span>
                <span className="font-semibold text-foreground text-sm capitalize mt-0.5 block">
                  {subscription?.billingCycle || "Monthly"}
                </span>
              </div>
            </div>
          </div>

          {/* Unlocked Features Matrix */}
          <div className="rounded-xl border border-border/80 bg-card p-6 shadow-xs space-y-4">
            <h3 className="text-sm font-bold text-foreground flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-500" />
              All Features Unlocked
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              {[
                "Custom Domain (drsmith.com)",
                "All 5 Luxury Bento Themes",
                "Custom Brand Hex Colors",
                "Canvas Width & Spacing Sliders",
                "BibTeX & DOI Ingestion",
                "Multi-Layout PDF CV Builder",
                "Research Poster & Slide Embeds",
                "Comprehensive Visitor Analytics",
              ].map((feat, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/20 border border-border/60 font-medium text-foreground"
                >
                  <Check className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span>{feat}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : isPro ? (
        /* ══════════════════════════════════════════════════════════════════════
            STATE 2: ACTIVE PRO - TARGETED UPGRADE TO PREMIUM ONLY
            ══════════════════════════════════════════════════════════════════════ */
        <div className="space-y-6">
          {/* Current Pro Plan Card */}
          <div className="rounded-xl border border-border/80 bg-card p-5 shadow-xs flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Current Membership</span>
                <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary/10 text-primary border border-primary/20">
                  Pro Tier
                </span>
                <span className="text-xs font-semibold text-emerald-500 flex items-center gap-1 ml-1">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Active
                </span>
              </div>
              <h2 className="text-lg font-bold text-foreground mt-1">Pro Scholar Membership</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {subscription?.expiresAt && (
                  <span>
                    Renews on {new Date(subscription.expiresAt).toLocaleDateString()} (
                    {subscription.daysRemaining} days remaining)
                  </span>
                )}
              </p>
            </div>

            <Button
              variant="outline"
              size="sm"
              disabled={isCancelling}
              onClick={() => {
                if (
                  confirm(
                    "Are you sure you want to cancel your Pro subscription? Your content will remain safely stored, but your public display will revert to Starter."
                  )
                ) {
                  setIsCancelling(true);
                  cancelSubscriptionMutation.mutate();
                }
              }}
              className="text-xs text-muted-foreground hover:text-destructive hover:border-destructive/40"
            >
              {isCancelling ? "Cancelling..." : "Cancel Subscription"}
            </Button>
          </div>

          {/* Targeted Upgrade Card to Premium */}
          <div className="rounded-2xl border-2 border-amber-500/40 bg-gradient-to-br from-amber-500/5 via-card to-card p-6 shadow-sm space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-extrabold text-white shadow-xs">
                  <Crown className="h-3 w-3" /> UPGRADE PATH
                </span>
                <h3 className="text-lg font-bold text-foreground mt-2">Elevate to Premium Scholar</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Unlock the PDF CV Builder, research poster embeds, and full visitor analytics.
                </p>
              </div>

              {/* Billing cycle toggle for upgrade */}
              <div className="flex items-center gap-1 p-1 bg-muted rounded-lg text-xs font-medium self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer ${
                    billingCycle === "monthly" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("annual")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                    billingCycle === "annual" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground"
                  }`}
                >
                  <span>Annual</span>
                  <span className="text-[10px] text-emerald-500 font-extrabold uppercase">Save ~17%</span>
                </button>
              </div>
            </div>

            {/* Premium feature unlock list */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-card/60 border border-border/60">
                <Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-foreground block">Multi-Layout PDF CV Builder</span>
                  <span className="text-muted-foreground text-[11px]">
                    Export publication-ready academic CVs with custom section ordering.
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-card/60 border border-border/60">
                <Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-foreground block">Research Poster & Slide Embeds</span>
                  <span className="text-muted-foreground text-[11px]">
                    Embed interactive Figma, Google Slides, and conference poster viewers.
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-card/60 border border-border/60">
                <Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-foreground block">Visitor & Profile Analytics</span>
                  <span className="text-muted-foreground text-[11px]">
                    Track citations, paper downloads, and regional academic visitor stats.
                  </span>
                </div>
              </div>
              <div className="flex items-start gap-2.5 p-3 rounded-lg bg-card/60 border border-border/60">
                <Sparkles className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                <div>
                  <span className="font-bold text-foreground block">Priority Academic Support</span>
                  <span className="text-muted-foreground text-[11px]">
                    Fast-track support for domain verification and institutional integrations.
                  </span>
                </div>
              </div>
            </div>

            {/* Upgrade action strip */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-xl bg-amber-500/10 border border-amber-500/20">
              <div>
                <span className="text-[10px] uppercase font-bold text-amber-600 dark:text-amber-400 block">
                  Upgrade Price ({billingCycle === "annual" ? "Annual" : "Monthly"})
                </span>
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl font-black text-foreground">
                    {billingConfig.currency}{" "}
                    {billingCycle === "annual"
                      ? premiumAnnual.formattedAmount
                      : premiumMonthly.formattedAmount}
                  </span>
                  {(billingCycle === "annual" ? premiumAnnual.hasDiscount : premiumMonthly.hasDiscount) && (
                    <span className="text-xs text-muted-foreground line-through">
                      {billingConfig.currency}{" "}
                      {billingCycle === "annual"
                        ? premiumAnnual.baseAmount.toLocaleString()
                        : premiumMonthly.baseAmount.toLocaleString()}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">
                    {billingCycle === "annual" ? "/ year" : "/ month"}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  onClick={() => handlePayHereCheckout("premium")}
                  disabled={isInitiatingPayHere}
                  className="text-xs font-bold gap-2 px-5 h-9 bg-amber-500 hover:bg-amber-600 text-white"
                >
                  {isInitiatingPayHere ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <>
                      <span>Upgrade to Premium</span>
                      <ArrowRight className="h-3.5 w-3.5" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ══════════════════════════════════════════════════════════════════════
            STATE 3 & 4: STARTER, PENDING, EXPIRED, OR REJECTED
            ══════════════════════════════════════════════════════════════════════ */
        <div className="space-y-8">
          {/* Expired Notification Banner */}
          {isExpired && (
            <div className="p-4 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs space-y-1">
              <div className="flex items-center gap-2 font-bold text-sm">
                <AlertCircle className="h-4 w-4" />
                <span>Subscription Expired</span>
              </div>
              <p>
                Your membership has expired. Your research data and publications are 100% safe, but your custom styling and domain are currently hidden from public visitors.
              </p>
            </div>
          )}

          {/* Pending Bank Review Card */}
          {isPending && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 flex items-center justify-center shrink-0">
                    <Clock className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-amber-700 dark:text-amber-400">
                      Bank Transfer Receipt Under Staff Verification
                    </h3>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Your transfer slip was received. Staff typically review and approve requests within a few hours.
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={isCancellingPending}
                  onClick={() => {
                    if (confirm("Are you sure you want to cancel this pending bank transfer?")) {
                      setIsCancellingPending(true);
                      cancelPendingMutation.mutate();
                    }
                  }}
                  className="text-xs text-muted-foreground hover:text-destructive hover:border-destructive/40"
                >
                  {isCancellingPending ? "Cancelling..." : "Cancel Request"}
                </Button>
              </div>

              {/* Submitted Metadata Summary */}
              <div className="p-4 rounded-lg bg-card/60 border border-border/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground block">Requested Tier</span>
                  <span className="font-bold text-foreground capitalize">
                    {subscription?.tier} ({subscription?.billingCycle || "Monthly"})
                  </span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground block">Reference #</span>
                  <span className="font-mono font-bold text-foreground">
                    {receiptMetadata.referenceNumber || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground block">Depositor</span>
                  <span className="font-semibold text-foreground">
                    {receiptMetadata.depositorName || "N/A"}
                  </span>
                </div>
                <div>
                  <span className="text-[10px] uppercase font-semibold text-muted-foreground block">Receipt Preview</span>
                  {subscription?.paymentReceiptUrl ? (
                    <button
                      type="button"
                      onClick={() => setLightboxUrl(subscription.paymentReceiptUrl)}
                      className="text-primary hover:underline font-semibold flex items-center gap-1 cursor-pointer"
                    >
                      <Eye className="h-3 w-3" /> View Slip
                    </button>
                  ) : (
                    <span className="text-muted-foreground">None</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rejected Transfer Alert */}
          {isRejected && (
            <div className="p-4 rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-xs space-y-1">
              <div className="flex items-center gap-2 font-bold text-sm">
                <XCircle className="h-4 w-4" />
                <span>Bank Transfer Not Approved</span>
              </div>
              <p>Reason: {subscription?.rejectionReason || "Receipt image unreadable or amount mismatch."}</p>
              <p className="font-medium text-foreground mt-1">
                Please re-submit a clear deposit slip below.
              </p>
            </div>
          )}

          {/* ── Three-Tier Comparison Cards ── */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
              <div>
                <h2 className="text-sm font-bold text-foreground">Choose Your Membership Tier</h2>
                <span className="text-xs text-muted-foreground">
                  Instant activation via PayHere or direct bank transfer
                </span>
              </div>

              {/* Billing Cycle Toggle */}
              <div className="flex items-center gap-1 p-1 bg-muted rounded-lg text-xs font-medium self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => setBillingCycle("monthly")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer ${
                    billingCycle === "monthly" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground"
                  }`}
                >
                  Monthly
                </button>
                <button
                  type="button"
                  onClick={() => setBillingCycle("annual")}
                  className={`px-3 py-1 rounded-md transition-all cursor-pointer flex items-center gap-1 ${
                    billingCycle === "annual" ? "bg-card text-foreground shadow-xs font-bold" : "text-muted-foreground"
                  }`}
                >
                  <span>Annual</span>
                  <span className="text-[10px] text-emerald-500 font-extrabold uppercase">Save ~17%</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Starter (Free) */}
              <div className="rounded-xl border border-border/80 bg-card p-5 flex flex-col justify-between shadow-xs">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-sm text-foreground">Starter</h3>
                    <span className="text-[10px] font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded-md">Free</span>
                  </div>
                  <div className="text-2xl font-extrabold text-foreground mb-1">
                    {billingConfig.currency} 0
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-4">Essential academic web presence.</p>

                  <ul className="space-y-2 text-xs text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>username.profolio.lk</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Classic & Minimalist Themes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>8 Curated Color Palettes</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>All Standard Academic Sections</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-6">
                  <Button variant="outline" size="sm" disabled className="w-full text-xs h-8">
                    Current Plan
                  </Button>
                </div>
              </div>

              {/* Pro */}
              <div
                onClick={() => setSelectedTier("pro")}
                className={`relative rounded-xl border-2 p-5 flex flex-col justify-between transition-all cursor-pointer shadow-xs ${
                  selectedTier === "pro"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border/80 bg-card hover:border-primary/40"
                }`}
              >
                <div className="absolute -top-3 right-4">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold text-primary-foreground shadow-xs">
                    <Zap className="h-3 w-3" /> POPULAR
                  </span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-sm text-foreground">Pro</h3>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-black text-foreground">
                      {billingConfig.currency}{" "}
                      {billingCycle === "annual" ? proAnnual.formattedAmount : proMonthly.formattedAmount}
                    </span>
                    {(billingCycle === "annual" ? proAnnual.hasDiscount : proMonthly.hasDiscount) && (
                      <span className="text-xs text-muted-foreground line-through">
                        {billingConfig.currency}{" "}
                        {billingCycle === "annual"
                          ? proAnnual.baseAmount.toLocaleString()
                          : proMonthly.baseAmount.toLocaleString()}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {billingCycle === "annual" ? " / year" : " / month"}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-4">Complete branding & visual freedom.</p>

                  <ul className="space-y-2 text-xs text-foreground">
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Custom Domain (drsmith.com)</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>All 5 Themes (Modern Bento, Grid)</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Custom Brand Hex Colors</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Canvas Width & Spacing Sliders</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>BibTeX & DOI Publication Ingestion</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-6">
                  <Button
                    size="sm"
                    variant={selectedTier === "pro" ? "default" : "outline"}
                    className="w-full text-xs h-8 font-semibold"
                  >
                    {selectedTier === "pro" ? "Selected for Upgrade" : "Select Pro"}
                  </Button>
                </div>
              </div>

              {/* Premium */}
              <div
                onClick={() => setSelectedTier("premium")}
                className={`relative rounded-xl border-2 p-5 flex flex-col justify-between transition-all cursor-pointer shadow-xs ${
                  selectedTier === "premium"
                    ? "border-primary bg-primary/5 ring-1 ring-primary"
                    : "border-border/80 bg-card hover:border-primary/40"
                }`}
              >
                <div className="absolute -top-3 right-4">
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white shadow-xs">
                    <Crown className="h-3 w-3" /> LUXURY
                  </span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-bold text-sm text-foreground">Premium</h3>
                  </div>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-2xl font-black text-foreground">
                      {billingConfig.currency}{" "}
                      {billingCycle === "annual" ? premiumAnnual.formattedAmount : premiumMonthly.formattedAmount}
                    </span>
                    {(billingCycle === "annual" ? premiumAnnual.hasDiscount : premiumMonthly.hasDiscount) && (
                      <span className="text-xs text-muted-foreground line-through">
                        {billingConfig.currency}{" "}
                        {billingCycle === "annual"
                          ? premiumAnnual.baseAmount.toLocaleString()
                          : premiumMonthly.baseAmount.toLocaleString()}
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {billingCycle === "annual" ? " / year" : " / month"}
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mb-4">Ultimate prestige suite for researchers.</p>

                  <ul className="space-y-2 text-xs text-foreground">
                    <li className="flex items-center gap-2 font-semibold text-primary">
                      <Sparkles className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <span>Everything in Pro +</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Multi-Layout PDF CV Builder</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Research Poster & Slide Embeds</span>
                    </li>
                    <li className="flex items-center gap-2 font-medium">
                      <Check className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>Full Profile & Visitor Analytics</span>
                    </li>
                  </ul>
                </div>

                <div className="pt-6">
                  <Button
                    size="sm"
                    variant={selectedTier === "premium" ? "default" : "outline"}
                    className="w-full text-xs h-8 font-semibold"
                  >
                    {selectedTier === "premium" ? "Selected for Upgrade" : "Select Premium"}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* ── Payment Method & Checkout Area ── */}
          <div className="rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs">
            <div className="px-6 py-4 border-b border-border/60 bg-muted/20 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-primary" />
                <h2 className="text-sm font-bold text-foreground">
                  Upgrade to {selectedTier === "pro" ? "Pro" : "Premium"} (
                  {billingConfig.currency} {currentPrice.formattedAmount} /{" "}
                  {billingCycle === "annual" ? "year" : "month"})
                </h2>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPaymentMethodTab("payhere")}
                  className={`px-3 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    paymentMethodTab === "payhere"
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Instant (PayHere)
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentMethodTab("bank")}
                  className={`px-3 py-1 rounded-md text-xs font-medium cursor-pointer transition-colors ${
                    paymentMethodTab === "bank"
                      ? "bg-primary text-primary-foreground font-bold shadow-xs"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Bank Transfer
                </button>
              </div>
            </div>

            <div className="p-6">
              {paymentMethodTab === "payhere" ? (
                <div className="space-y-6">
                  <div className="rounded-xl p-5 border border-border/80 bg-muted/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-sm text-foreground">PayHere Instant Online Checkout</span>
                        <span className="text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 font-bold px-2 py-0.5 rounded-full border border-emerald-500/20">
                          Instant Entitlement
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Supports Visa, MasterCard, Genie, FriMi, eZ Cash, and mCash. Pro features unlock immediately upon return.
                      </p>
                    </div>
                    <Button
                      onClick={() => handlePayHereCheckout()}
                      disabled={isInitiatingPayHere}
                      className="w-full sm:w-auto text-xs font-bold gap-2 px-5 h-9"
                    >
                      {isInitiatingPayHere ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          <span>Connecting Gateway...</span>
                        </>
                      ) : (
                        <>
                          <span>Pay {billingConfig.currency} {currentPrice.formattedAmount}</span>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="flex items-center gap-2 text-[11px] text-muted-foreground px-1">
                    <ShieldCheck className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                    <span>
                      Payments are 256-bit SSL encrypted and securely processed by PayHere (Central Bank of Sri Lanka approved).
                    </span>
                  </div>
                </div>
              ) : (
                /* Interactive 3-Step Bank Receipt Upload Flow */
                <div className="space-y-6">
                  {/* Step Indicators */}
                  <div className="grid grid-cols-3 gap-2 text-center text-xs pb-3 border-b border-border/60">
                    <div
                      className={`p-2 rounded-lg font-bold cursor-pointer ${
                        bankStep === 1 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      }`}
                      onClick={() => setBankStep(1)}
                    >
                      1. Bank Details
                    </div>
                    <div
                      className={`p-2 rounded-lg font-bold cursor-pointer ${
                        bankStep === 2 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      }`}
                      onClick={() => setBankStep(2)}
                    >
                      2. Receipt & Metadata
                    </div>
                    <div
                      className={`p-2 rounded-lg font-bold cursor-pointer ${
                        bankStep === 3 ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      }`}
                      onClick={() => {
                        if (selectedFile && referenceNumber && depositorName) setBankStep(3);
                      }}
                    >
                      3. Confirm
                    </div>
                  </div>

                  {/* STEP 1: Bank Account Details with 1-Click Copy */}
                  {bankStep === 1 && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 rounded-xl bg-muted/30 border border-border/80 text-xs">
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Bank & Branch</span>
                          <span className="font-semibold text-foreground">
                            {billingConfig.bankDetails.bankName} • {billingConfig.bankDetails.branch}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Account Name</span>
                          <span className="font-semibold text-foreground">
                            {billingConfig.bankDetails.accountName}
                          </span>
                        </div>
                        <div className="flex items-center justify-between pr-2">
                          <div>
                            <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Account Number</span>
                            <span className="font-mono font-bold text-foreground">
                              {billingConfig.bankDetails.accountNumber}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                billingConfig.bankDetails.accountNumber.replace(/\s+/g, ""),
                                "Account number"
                              )
                            }
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground cursor-pointer"
                            title="Copy account number"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                        <div className="flex items-center justify-between pr-2">
                          <div>
                            <span className="text-muted-foreground block text-[10px] uppercase font-semibold">Amount to Deposit</span>
                            <span className="font-mono font-bold text-primary">
                              {billingConfig.currency} {currentPrice.formattedAmount}
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              copyToClipboard(currentPrice.discountedAmount.toString(), "Amount")
                            }
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground cursor-pointer"
                            title="Copy amount"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>

                      {billingConfig.bankDetails.instructions && (
                        <p className="text-[11px] text-muted-foreground italic px-1">
                          {billingConfig.bankDetails.instructions}
                        </p>
                      )}

                      <div className="flex justify-end pt-2">
                        <Button
                          onClick={() => setBankStep(2)}
                          className="text-xs font-bold gap-2 px-5"
                        >
                          <span>Next: Upload Receipt & Details</span>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* STEP 2: Drag & Drop Dropzone, Preview Thumbnail & Metadata Form */}
                  {bankStep === 2 && (
                    <div className="space-y-5">
                      {/* Dropzone with Client-side Preview */}
                      {!selectedFile ? (
                        <div className="border-2 border-dashed border-border/80 hover:border-primary/50 transition-colors rounded-xl p-8 text-center bg-card flex flex-col items-center justify-center gap-2 relative cursor-pointer">
                          <input
                            type="file"
                            accept="image/png, image/jpeg, image/webp, application/pdf"
                            onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
                            className="absolute inset-0 opacity-0 cursor-pointer"
                          />
                          <div className="h-12 w-12 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                            <UploadCloud className="h-6 w-6" />
                          </div>
                          <div className="space-y-0.5">
                            <p className="text-xs font-bold text-foreground">Click to browse or drop transfer receipt here</p>
                            <p className="text-[11px] text-muted-foreground">PNG, JPG, WebP, or PDF up to 5MB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 rounded-xl border border-border/80 bg-muted/20 flex flex-col sm:flex-row items-center justify-between gap-4">
                          <div className="flex items-center gap-3 w-full sm:w-auto">
                            {previewObjectUrl && !selectedFile.type.includes("pdf") ? (
                              <img
                                src={previewObjectUrl}
                                alt="Selected Receipt Thumbnail"
                                className="h-16 w-16 object-cover rounded-lg border border-border shadow-xs"
                              />
                            ) : (
                              <div className="h-16 w-16 rounded-lg bg-primary/10 text-primary flex items-center justify-center border border-border">
                                <FileText className="h-8 w-8" />
                              </div>
                            )}
                            <div className="truncate">
                              <span className="text-xs font-bold text-foreground block truncate">
                                {selectedFile.name}
                              </span>
                              <span className="text-[11px] text-muted-foreground block">
                                {(selectedFile.size / 1024).toFixed(1)} KB • {selectedFile.type || "Document"}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                            {previewObjectUrl && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => setLightboxUrl(previewObjectUrl)}
                                className="text-xs gap-1.5 h-8"
                              >
                                <Eye className="h-3.5 w-3.5" />
                                <span>Preview Full Size</span>
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleFileChange(null)}
                              className="text-xs text-destructive hover:bg-destructive/10 h-8"
                            >
                              Change File
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Required Metadata Input Fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                        <div className="space-y-1">
                          <label className="font-semibold text-foreground block">
                            Bank Transaction Reference # <span className="text-destructive">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. FT261028123456"
                            value={referenceNumber}
                            onChange={(e) => setReferenceNumber(e.target.value)}
                            className="w-full p-2.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-semibold text-foreground block">
                            Depositor / Account Holder Name <span className="text-destructive">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Dr. A. Perera"
                            value={depositorName}
                            onChange={(e) => setDepositorName(e.target.value)}
                            className="w-full p-2.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-semibold text-foreground block">Date of Transfer</label>
                          <input
                            type="date"
                            value={transferDate}
                            onChange={(e) => setTransferDate(e.target.value)}
                            className="w-full p-2.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="font-semibold text-foreground block">Additional Notes / Branch (Optional)</label>
                          <input
                            type="text"
                            placeholder="e.g. Kandy Branch deposit"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="w-full p-2.5 rounded-lg border border-border bg-background text-foreground text-xs focus:ring-1 focus:ring-primary focus:outline-hidden"
                          />
                        </div>
                      </div>

                      <div className="flex justify-between pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setBankStep(1)}
                          className="text-xs"
                        >
                          Back to Bank Info
                        </Button>
                        <Button
                          onClick={() => {
                            if (!selectedFile) {
                              toast.error("Please select a receipt file");
                              return;
                            }
                            if (!referenceNumber.trim()) {
                              toast.error("Please enter the reference number");
                              return;
                            }
                            if (!depositorName.trim()) {
                              toast.error("Please enter the depositor's name");
                              return;
                            }
                            setBankStep(3);
                          }}
                          className="text-xs font-bold gap-2 px-5"
                        >
                          <span>Review & Confirm</span>
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Review Summary & Submit */}
                  {bankStep === 3 && (
                    <div className="space-y-5">
                      <div className="p-4 rounded-xl border border-border/80 bg-muted/20 space-y-3 text-xs">
                        <h4 className="font-bold text-foreground text-sm border-b border-border/60 pb-2">
                          Summary of Your Transfer
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Tier & Cycle:</span>
                            <span className="font-bold text-foreground capitalize">
                              {selectedTier} ({billingCycle})
                            </span>
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Amount:</span>
                            <span className="font-bold text-primary">
                              {billingConfig.currency} {currentPrice.formattedAmount}
                            </span>
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Reference #:</span>
                            <span className="font-mono font-bold text-foreground">{referenceNumber}</span>
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Depositor:</span>
                            <span className="font-semibold text-foreground">{depositorName}</span>
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Date:</span>
                            <span className="font-semibold text-foreground">{transferDate}</span>
                          </div>
                          <div>
                            <span className="block text-[10px] uppercase font-semibold">Receipt File:</span>
                            <span className="font-semibold text-foreground truncate block">
                              {selectedFile?.name}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-between pt-2">
                        <Button
                          variant="outline"
                          onClick={() => setBankStep(2)}
                          disabled={isUploading}
                          className="text-xs"
                        >
                          Back to Edit
                        </Button>
                        <Button
                          onClick={handleBankTransferSubmit}
                          disabled={isUploading}
                          className="text-xs font-bold gap-2 px-6 h-9"
                        >
                          {isUploading ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              <span>Submitting Receipt...</span>
                            </>
                          ) : (
                            <>
                              <span>Submit Receipt for Approval</span>
                              <ArrowRight className="h-3.5 w-3.5" />
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── High-Resolution Receipt Viewer Lightbox Modal ── */}
      <ReceiptViewerModal
        open={Boolean(lightboxUrl)}
        onOpenChange={(open) => !open && setLightboxUrl(null)}
        receiptUrl={lightboxUrl}
        metadata={
          receiptMetadata.referenceNumber
            ? {
                referenceNumber: receiptMetadata.referenceNumber,
                depositorName: receiptMetadata.depositorName,
                amount: receiptMetadata.amount,
                currency: receiptMetadata.currency,
                transferDate: receiptMetadata.transferDate,
              }
            : undefined
        }
      />
    </div>
  );
}
